import { useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { faArrowUpRightFromSquare, faMemo } from '@fortawesome/pro-regular-svg-icons';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateAccountBillingSubscription } from 'api/subscriptions';
import { ChatWithSales } from 'assets/js/pages/account-settings/account-about/givebutter-plus/ChatWithSales';
import { CurrentPlanCard } from 'assets/js/pages/account-settings/account-about/givebutter-plus/CurrentPlanCard';
import { NextBillDueCallout } from 'assets/js/pages/account-settings/account-about/givebutter-plus/NextBillDueCallout';
import { OverageMessage } from 'assets/js/pages/account-settings/account-about/givebutter-plus/OverageMessage';
import { OveragesWarningPrompt } from 'assets/js/pages/account-settings/account-about/givebutter-plus/OveragesWarningPrompt';
import { SubscriptionFaqs } from 'assets/js/pages/account-settings/account-about/givebutter-plus/SubscriptionFaqs';
import * as SubscriptionIntervalRadioGroup from 'assets/js/pages/account-settings/account-about/givebutter-plus/SubscriptionIntervalRadioGroup';
import { SubscriptionPlanLineItems } from 'assets/js/pages/account-settings/account-about/givebutter-plus/SubscriptionPlanLineItems';
import { SubscriptionPlanLineItemsDetailed } from 'assets/js/pages/account-settings/account-about/givebutter-plus/SubscriptionPlanLineItemsDetailed';
import { SubscriptionSettingsFormValues } from 'assets/js/pages/account-settings/account-about/givebutter-plus/types';
import { useSubscriptionFormTable } from 'assets/js/pages/account-settings/account-about/givebutter-plus/useSubscriptionFormTable';
import { useSubscriptionPlans } from 'assets/js/pages/account-settings/account-about/givebutter-plus/useSubscriptionPlans';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import * as Table from 'components/table';
import { AnchorHashIds } from 'constants/anchor-hash-ids';
import { posthogEvents } from 'constants/posthog-events';
import { useAppState } from 'customHooks/useAppState';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { usePosthog } from 'customHooks/usePosthog';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { SubscriptionTier } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const SubscriptionFormEdit = () => {
    const { apiUrl } = useAppState();
    const { accountId, validGivebutterPlusPlan } = useCurrentAccount();
    const { success, error } = useToast();
    const { data: plans } = useSubscriptionPlans();
    const { pushEvent } = usePosthog();

    const currentTier = validGivebutterPlusPlan?.tier;
    const defaultPlanId =
        plans.find(plan => plan.contacts_max === currentTier?.max_contacts)?.id ?? '';
    const isOnAnnualPlan = currentTier?.interval === 'year';

    const methods = Form.useGivebutterForm<SubscriptionSettingsFormValues>({
        defaultValues: {
            tier: defaultPlanId as SubscriptionTier,
            billingPeriod: currentTier?.interval,
        },
    });

    const { control, path, watch, resetFormButton, getValues } = methods;
    const [billingPeriod] = watch(['billingPeriod']);
    const showAnnualPricing = billingPeriod === 'year';

    const { table } = useSubscriptionFormTable({
        defaultPlanId,
        showAnnualPricing,
        currentPlanId: defaultPlanId,
    });
    const rowSelection = table.getState().rowSelection;

    const selectedPlan = useMemo(
        () => plans.find(plan => rowSelection[plan.id]),
        [plans, rowSelection],
    );
    const showChatWithSalesMsg = useMemo(() => {
        if (!selectedPlan) {
            return false;
        }
        const selectedIndex = plans.findIndex(plan => plan.id === selectedPlan.id);
        return selectedIndex >= plans.length - 2;
    }, [selectedPlan, plans]);

    const isEdited = useMemo(() => {
        return (
            currentTier?.interval !== billingPeriod ||
            currentTier.max_contacts !== selectedPlan?.contacts_max
        );
    }, [billingPeriod, currentTier, selectedPlan]);

    const { mutate: submit, isPending } = useMutation<
        unknown,
        GivebutterAxiosError,
        SubscriptionSettingsFormValues
    >({
        mutationFn: () =>
            updateAccountBillingSubscription(accountId, {
                tier: selectedPlan?.id as SubscriptionTier,
                billing_period: billingPeriod,
            }),
        onSuccess: () => {
            success('Plan successfully updated!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.current(accountId),
            });
        },
        onError: e => {
            error(e.response?.data.message ?? 'Unknown error occurred');
        },
        onSettled: () => {
            resetFormButton();
        },
    });

    if (!validGivebutterPlusPlan) {
        return null;
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex justify-between">
                <p className="text-xl font-medium text-gray-700">Subscription & Billing</p>
                <a
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-center text-sm font-medium text-gray-900 hover:bg-gray-100 hover:text-gray-900"
                    href={`${apiUrl}/dashboard/accounts/${accountId}/billing`}
                >
                    Manage billing
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="sm" />
                </a>
            </div>
            <NextBillDueCallout />
            <OveragesWarningPrompt />
            <Box className="flex flex-col gap-6 p-6">
                <div className="w-full">
                    <CurrentPlanCard />
                </div>

                <Separator className="bg-gray-200" />
                <div
                    className="flex scroll-mt-24 flex-col"
                    id={AnchorHashIds.ManageGivebutterPlusPlan}
                >
                    <p className="font-medium">Manage Givebutter Plus plan</p>
                    <p className="text-sm">
                        {currentTier?.interval === 'year'
                            ? 'Select a new plan below. You will be billed immediately for the prorated difference between your current and existing plan. You’re unable to change your plan to a monthly plan.'
                            : 'Select a new plan below. You will be billed immediately for your upgraded plan. If you switch to an annual plan, this bill will be for the full cost of your annual subscription less a prorated credit for your current subscription period.'}
                    </p>
                </div>
                <Form.Provider {...methods}>
                    <form className="flex flex-wrap gap-8 2xl:flex-nowrap">
                        <div className="flex w-full flex-col gap-3">
                            <SubscriptionIntervalRadioGroup.Root
                                control={control}
                                name={path('billingPeriod')}
                            >
                                <SubscriptionIntervalRadioGroup.Monthly disabled={isOnAnnualPlan} />
                                <SubscriptionIntervalRadioGroup.Annually />
                            </SubscriptionIntervalRadioGroup.Root>

                            <Table.Root variant={'bordered'} table={table}>
                                <div className={'flex flex-col gap-4'}>
                                    <Box className={'overflow-x-auto'}>
                                        <Table.Content>
                                            <Table.Header className="text-xs text-gray-600" />
                                            <Table.Body />
                                        </Table.Content>
                                    </Box>
                                </div>
                            </Table.Root>
                            <OverageMessage billingPeriod={billingPeriod} />
                        </div>
                        {selectedPlan && (
                            <div className="flex grow flex-col gap-4">
                                <SubscriptionPlanLineItems
                                    plan={selectedPlan}
                                    showAnnualPricing={showAnnualPricing}
                                />
                                <Dialog.Root>
                                    <Dialog.Trigger asChild>
                                        <Button
                                            variant="solid"
                                            disabled={!isEdited}
                                            onClick={() => {
                                                pushEvent(
                                                    posthogEvents.DASHBOARD_BILLING_PLAN_UPDATE_SELECTED,
                                                    {
                                                        billingCycle: showAnnualPricing
                                                            ? 'year'
                                                            : 'month',
                                                        planId: selectedPlan.id,
                                                    },
                                                );
                                            }}
                                        >
                                            Update plan
                                        </Button>
                                    </Dialog.Trigger>
                                    <Dialog.Portal>
                                        <Dialog.Overlay />
                                        <Dialog.Content className="flex max-w-sm flex-col gap-2">
                                            <Dialog.Header>
                                                <div className="relative flex aspect-square h-8 items-center justify-center rounded-full text-white after:absolute after:-inset-1 after:-z-[1] after:rounded-full after:bg-gray-100 after:p-2">
                                                    <FontAwesomeIcon
                                                        icon={faMemo}
                                                        className="text-gray-400"
                                                    />
                                                </div>

                                                <Dialog.Close />
                                            </Dialog.Header>
                                            <Dialog.Body className="gap-6">
                                                <div className="flex flex-col gap-2">
                                                    <Dialog.Title className="text-xl sm:text-xl">
                                                        Update plan?
                                                    </Dialog.Title>
                                                    <Dialog.Description>
                                                        {showAnnualPricing
                                                            ? 'For an annual plan, you will be billed immediately for the full cost of your annual subscription less a prorated credit for your current subscription period.'
                                                            : `You will be billed immediately for the prorated difference between your current and new plan.`}
                                                    </Dialog.Description>
                                                </div>
                                                <SubscriptionPlanLineItemsDetailed
                                                    plan={selectedPlan}
                                                    showAnnualPricing={showAnnualPricing}
                                                />
                                                <div className="flex w-full gap-2">
                                                    <Dialog.Cancel asChild>
                                                        <Button
                                                            variant="outline"
                                                            color="neutral"
                                                            className="flex-grow"
                                                            onClick={() => {
                                                                pushEvent(
                                                                    posthogEvents.DASHBOARD_BILLING_PLAN_UPDATE_CANCELLED,
                                                                    {
                                                                        billingCycle:
                                                                            showAnnualPricing
                                                                                ? 'year'
                                                                                : 'month',
                                                                        planId: selectedPlan.id,
                                                                    },
                                                                );
                                                            }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </Dialog.Cancel>
                                                    <Button
                                                        variant="solid"
                                                        className="flex-grow"
                                                        onClick={() => {
                                                            submit(getValues());
                                                            pushEvent(
                                                                posthogEvents.DASHBOARD_BILLING_PLAN_UPDATE_SUBMITTED,
                                                                {
                                                                    billingCycle: showAnnualPricing
                                                                        ? 'year'
                                                                        : 'month',
                                                                    planId: selectedPlan.id,
                                                                },
                                                            );
                                                        }}
                                                        disabled={isPending}
                                                    >
                                                        {isPending && (
                                                            <FontAwesomeIcon
                                                                icon={faSpinnerThird}
                                                                className="animate-spin"
                                                            />
                                                        )}
                                                        Update
                                                    </Button>
                                                </div>
                                            </Dialog.Body>
                                        </Dialog.Content>
                                    </Dialog.Portal>
                                </Dialog.Root>
                                {showChatWithSalesMsg && <ChatWithSales />}
                            </div>
                        )}
                    </form>
                </Form.Provider>
            </Box>
            <SubscriptionFaqs />
        </div>
    );
};

export { SubscriptionFormEdit };
